
import { supabase } from "@/integrations/supabase/client";
import { logError, logInfo, logDebug } from '@/utils/logging';

// Define Conversation Message type
export interface ConversationMessage {
  id: string;
  sender: 'user' | 'ai';
  content: string;
  timestamp: string;
  userId: string;
}

// Save conversation to Supabase
export const saveConversation = async (sessionId: string, messages: ConversationMessage[]): Promise<void> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;
  
  // Prepare messages for insertion
  const messagesToInsert = messages.map(message => ({
    session_id: sessionId,
    sender: message.sender,
    content: message.content,
    timestamp: message.timestamp,
    user_id: user.id
  }));
  
  // First, delete existing messages for this session to avoid duplicates
  const { error: deleteError } = await supabase
    .from('conversation_messages')
    .delete()
    .eq('session_id', sessionId)
    .eq('user_id', user.id);
    
  if (deleteError) {
    logError('Error deleting existing messages:', deleteError);
    return;
  }
  
  // Then insert the new messages
  if (messagesToInsert.length > 0) {
    const { error: insertError } = await supabase
      .from('conversation_messages')
      .insert(messagesToInsert);
      
    if (insertError) {
      logError('Error saving conversation:', insertError);
    }
  }
};

// Get conversation by session ID
export const getConversationBySessionId = async (sessionId: string): Promise<ConversationMessage[]> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return [];
  
  // Use explicit column selection instead of '*'
  const { data, error } = await supabase
    .from('conversation_messages')
    .select('id, sender, content, timestamp, user_id')
    .eq('session_id', sessionId)
    .eq('user_id', user.id)
    .order('timestamp', { ascending: true });
    
  if (error) {
    logError('Error fetching conversation:', error);
    return [];
  }
  
  // Transform to our ConversationMessage interface
  return data.map(message => ({
    id: message.id,
    sender: message.sender as 'user' | 'ai',
    content: message.content,
    timestamp: message.timestamp,
    userId: message.user_id
  }));
};

// Generate a title for a session based on conversation
export const generateSessionTitle = async (
  sessionId: string, 
  bookTitle: string, 
  bookAuthor: string
): Promise<string | null> => {
  try {
    const conversation = await getConversationBySessionId(sessionId);
    
    if (!conversation || conversation.length < 3) {
      // Not enough conversation to generate a meaningful title
      return null;
    }
    
    // Call the Edge Function to generate a title using Supabase client
    const { data, error } = await supabase.functions.invoke('generate-session-title', {
      body: {
        conversation,
        bookTitle,
        bookAuthor
      }
    });
    
    if (error) {
      logError('Error generating session title:', error);
      return null;
    }
    
    return data.title;
  } catch (error) {
    logError('Failed to generate session title:', error);
    return null;
  }
};

// Generate a summary for a session
export const generateSessionSummary = async (
  sessionId: string,
  bookTitle: string,
  bookAuthor: string
): Promise<{summary: string, topics: string[]} | null> => {
  try {
    // Call the Edge Function to generate a summary
    const { data, error } = await supabase.functions.invoke('generate-session-summary', {
      body: {
        sessionId,
        bookTitle,
        bookAuthor
      }
    });
    
    if (error) {
      logError('Error generating session summary:', error);
      return null;
    }
    
    if (!data || !data.success) {
      logError('Generate summary did not return success:', data);
      return null;
    }
    
    return {
      summary: data.summary,
      topics: data.topics
    };
  } catch (error) {
    logError('Failed to generate session summary:', error);
    return null;
  }
};

// Generate title on session exit if there are enough messages
export const generateTitleOnExit = async (
  sessionId: string,
  bookTitle: string,
  bookAuthor: string
): Promise<void> => {
  try {
    const conversation = await getConversationBySessionId(sessionId);
    
    // Only generate title if we have enough conversation (at least 3 messages)
    if (conversation && conversation.length >= 3) {
      const title = await generateSessionTitle(sessionId, bookTitle, bookAuthor);
      if (title) {
        // Import here to avoid circular dependency
        const { updateSessionTitle } = await import('./sessionStore');
        await updateSessionTitle(sessionId, title);
      }
      
      // Generate session summary in the background
      generateSessionSummary(sessionId, bookTitle, bookAuthor)
        .then(result => {
          if (result) {
            logInfo(`Generated summary for session ${sessionId}:`, { 
              summaryLength: result.summary.length,
              topics: result.topics
            });
          }
        })
        .catch(error => {
          logError('Error in background summary generation:', error);
        });
    }
  } catch (error) {
    logError('Failed to generate title on exit:', error);
  }
};

// Clear conversation by session ID
export const clearConversationBySessionId = async (sessionId: string): Promise<void> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;
  
  const { error } = await supabase
    .from('conversation_messages')
    .delete()
    .eq('session_id', sessionId)
    .eq('user_id', user.id);
    
  if (error) {
    logError('Error clearing conversation:', error);
  }
};

// Get total message count for the current user
export const getTotalMessageCount = async (): Promise<number> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return 0;
  
  // Use count only query instead of selecting all data
  const { count, error } = await supabase
    .from('conversation_messages')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', user.id);
    
  if (error) {
    logError('Error counting messages:', error);
    return 0;
  }
  
  return count || 0;
};

// Add a helper to migrate localStorage data to the database
export const migrateLocalStorageToDatabase = async (): Promise<void> => {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return;
  
  try {
    // First, check if user already has data in the database
    const { count: bookCount, error: countError } = await supabase
      .from('books')
      .select('id', { count: 'exact', head: true })
      .eq('user_id', user.id);
      
    if (countError) {
      logError('Error checking existing books:', countError);
      return;
    }
      
    if (bookCount && bookCount > 0) {
      // User already has data in the database, skip migration
      return;
    }
    
    // Find all localStorage items that belong to this user
    const userId = user.id;
    const userPrefix = `bookvibes-books-${userId}`;
    const sessionPrefix = `bookvibes-session-${userId}-`;
    const bookSessionsPrefix = `bookvibes-book-sessions-${userId}-`;
    const conversationPrefix = `bookvibes-conversation-${userId}-`;
    
    // Migrate books
    const booksJson = localStorage.getItem(userPrefix);
    if (booksJson) {
      try {
        const books = JSON.parse(booksJson);
        if (Array.isArray(books) && books.length > 0) {
          const booksToInsert = books.map((book: any) => ({
            title: book.title,
            author: book.author,
            cover_url: book.coverUrl,
            description: book.description,
            category: book.category,
            rating: book.rating,
            review_summary: book.reviewSummary,
            amazon_url: book.amazonUrl,
            pages: book.pages,
            added_at: book.addedAt,
            user_id: userId
          }));
          
          const { error: insertError } = await supabase
            .from('books')
            .insert(booksToInsert);
            
          if (insertError) {
            logError('Error migrating books:', insertError);
          }
        }
      } catch (e) {
        logError('Error parsing books from localStorage:', e);
      }
    }
    
    // Migration complete, clear localStorage to avoid duplicates
    // This is a simplified example - a real migration would need to handle sessions and conversations too
    
  } catch (error) {
    logError('Error during migration:', error);
  }
};
